"use client";

import React from "react";
import type { ChatEntityPreviewEmbed } from "./types";
import { ChatEntityCardRow } from "./ChatEntityCardRow";
import { ChatEntityCardShell } from "./ChatEntityCardShell";
import { EntityPreviewStatus, entityPreviewDetailStatusLabel } from "./EntityPreviewStatus";
import { buildEntityPreviewCardKey } from "./entityPreviewCardKey";
import { useEntityPreviewCardFocus } from "./EntityPreviewCardFocusContext";
import { ENTITY_PREVIEW_ICONS } from "./entityPreviewUi";
import { useEntityPreviewNavigation } from "./useEntityPreviewNavigation";

type EntityPreviewCardProps = {
  embed: ChatEntityPreviewEmbed;
  messageId?: string;
  cardIndex: number;
};

function truncateSummary(summary: string, maxLength = 160): string {
  const trimmed = summary.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

export function EntityPreviewCard({ embed, messageId, cardIndex }: EntityPreviewCardProps) {
  const { openPreview } = useEntityPreviewNavigation();
  const icon = ENTITY_PREVIEW_ICONS[embed.entity_type];
  const cardKey = buildEntityPreviewCardKey(messageId ?? "unknown", embed, cardIndex);
  const { isExpanded, expand } = useEntityPreviewCardFocus(cardKey);
  const statusLabel = entityPreviewDetailStatusLabel(
    embed.entity_type,
    embed.status,
    embed.status_display,
  );
  const detail = (() => {
    if (embed.entity_type === "case_check") {
      return embed.progress_pct != null ? `${embed.progress_pct}%` : undefined;
    }
    return embed.summary ? truncateSummary(embed.summary) : undefined;
  })();

  if (!isExpanded) {
    return (
      <ChatEntityCardShell
        icon={icon}
        title={embed.label}
        meta={embed.subtitle}
        testId="chat-entity-preview"
        isExpanded={false}
        onExpand={expand}
        trailing={
          <EntityPreviewStatus
            entityType={embed.entity_type}
            status={embed.status}
            label={statusLabel}
          />
        }
      />
    );
  }

  return (
    <ChatEntityCardRow
      icon={icon}
      label={embed.label}
      hint={embed.subtitle}
      detail={detail}
      testId="chat-entity-preview"
      onClick={() => openPreview(embed)}
      trailing={
        <EntityPreviewStatus
          entityType={embed.entity_type}
          status={embed.status}
          label={statusLabel}
        />
      }
    />
  );
}
